import { useEffect, useState, useRef } from 'react';
import styles from './styles.module.scss';
import businessCategories from '../../../Data/businessCategories';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faUpload, faXmarkCircle, faSave, faTools, faCartShopping, faLink, faLocationDot, faEnvelope, faPhone, faMapLocationDot, faDoorOpen, faMoneyCheckDollar, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { BounceLoader, PuffLoader } from 'react-spinners';
import { motion } from 'framer-motion';
import Resizer from 'react-image-file-resizer';
import Image from 'next/image';
import errorMsg from '../../../../Functions/errorMsg';
import successMsg from '../../../../Functions/successMsg';
import infoMsg from '../../../../Functions/infoMsg';
import NumberFormat from 'react-number-format';
import { useSession } from 'next-auth/react';
import axios from 'axios';
import _ from 'lodash';
import ToggleSwitch from '../../../../FunctionalComponents/ToggleSwitch';
import { faFacebook, faGoogle, faInstagram, faYelp } from '@fortawesome/free-brands-svg-icons';
import Textarea from '../../../../FunctionalComponents/CustomTextarea/Textarea';
import TextInput from '../../../../FunctionalComponents/CustomTextInput/TextInput'

import generalSettings from './generalSettings';

export default function Settings({ sendHeaderData }) {
  const { data: session, status } = useSession();
  
  const [hasError, setHasError] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [ogSettingsData, setOGSettingsData] = useState(null);
  const [newSettingsData, setNewSettingsData] = useState(null);
  const [canSave, setCanSave] = useState(false);
  const [canRevert, setCanRevert] = useState(false);
  const [showSaveIcons, setShowSaveIcons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const settingsWrapperRef = useRef(null);

  const { aboutUsMRC, privacyPolicyMRC, returnPolicyMRC, cancellationPolicyMRC, faqMRC } = generalSettings;

  // Fetch settings data on mount
  useEffect(() => {
    if (!session || status !== 'authenticated' || hasError || loadingData || ogSettingsData || newSettingsData) return;

    async function verifyAccountCreated() {
      setLoadingData(true);
      try {
        const getResult = await axios.get(`${window.location.origin}/api/vendor/manage/settings/settingsData`);

        if (getResult && getResult.status === 200) {
          setOGSettingsData(getResult.data);
          setNewSettingsData(_.cloneDeep(getResult.data));
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true);
      } finally {
        setLoadingData(false);
      }
    }

    verifyAccountCreated();
  }, [session, status, hasError, loadingData, ogSettingsData, newSettingsData]);

  // Send header data if changes are made
  useEffect(() => {
    if (canRevert && newSettingsData) {
      sendHeaderData({
        pfp: newSettingsData.pfp,
        displayName: newSettingsData.displayName,
      });
    }
  }, [newSettingsData, canRevert]);

  useEffect(() => {
    const handleScroll = _.debounce(() => {
      if (settingsWrapperRef.current) {
        const wrapper = settingsWrapperRef.current;
        const isScrolledToBottom =
          wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - 10; // 10px buffer for smooth detection
  
        // Update `showSaveIcons` based on scroll position and `canSave`
        if (canSave && !isScrolledToBottom && !showSaveIcons) {
          setShowSaveIcons(true);
        } else if ((!canSave || isScrolledToBottom) && showSaveIcons) {
          setShowSaveIcons(false);
        }
      }
    }, 15); // 100ms debounce for performance
  
    const wrapper = settingsWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll);
    }
  
    return () => {
      if (wrapper) {
        wrapper.removeEventListener('scroll', handleScroll);
      }
    };
  }, [canSave, showSaveIcons, settingsWrapperRef]);
  
  

  useEffect(() => {
    if (!canSave) {
      setShowSaveIcons(false);
    }
  }, [canSave]);
  
  
  

  // Validate policies before saving
  const validatePolicies = () => {
    const policies = [
      { key: 'privacyPolicy', mrc: privacyPolicyMRC, label: 'Privacy Policy' },
      { key: 'returnPolicy', mrc: returnPolicyMRC, label: 'Return Policy' },
      { key: 'cancellationPolicy', mrc: cancellationPolicyMRC, label: 'Cancellation Policy' },
      { key: 'shopFAQs', mrc: faqMRC, label: 'FAQs' },
    ];

    for (const { key, mrc } of policies) {
      const policy = newSettingsData.shopInfo[key];
      // Only validate if the policy is enabled
      if (policy?.enabled) {
        if (policy.text.trim().length < mrc.min) {
          return false;
        }
      }
    }

    return true;
  };

  const isShopVisibleEnabled = () => {
    const { pfp, displayName, category, aboutUs, contactInfo } = newSettingsData;
  
    // Character constraints
    const displayNameMin = 5;
    const displayNameMax = 32;
    const aboutUsMin = 15;
    const aboutUsMax = 1000;
  
    return (
      !!pfp &&
      displayName.trim().length >= displayNameMin &&
      displayName.trim().length <= displayNameMax &&
      category &&
      aboutUs.trim().length >= aboutUsMin &&
      aboutUs.trim().length <= aboutUsMax &&
      contactInfo.phone.trim().length > 0 &&
      contactInfo.email.trim().length > 0 &&
      contactInfo.location.trim().length > 0
    );
  };
  

  // Check for changes and validate policies
  const checkForChanges = () => {
    const hasChanges = !_.isEqual(ogSettingsData, newSettingsData);
    setCanRevert(hasChanges); // Enable "Revert Changes" if there are any changes

    // Ensure Save Changes considers both validation and policy enablement
    const meetsValidation = validatePolicies();
    setCanSave(hasChanges && meetsValidation);
  };

  useEffect(() => {
    if (ogSettingsData && newSettingsData) {
      checkForChanges();
    }
  }, [newSettingsData, ogSettingsData]);
    

    const handleSubmit = async () => {
      if (!canSave || isSaving) return;

      // Check if Shop Visible is enabled but required fields are not filled
      if (newSettingsData.pagesEnabled.shop && !isShopVisibleEnabled()) {
        infoMsg('Before going live, please fill in brand & contact sections');
        return;
      }

      if (!validatePolicies()) return;
  
      setIsSaving(true);
      setLoadingData(true);
  
      try {
        const result = await axios.post(`${window.location.origin}/api/vendor/manage/settings/updateSettings`, {
          ...newSettingsData,
        });
  
        if (result && result.status === 200) {
          setOGSettingsData(result.data);
          setNewSettingsData(_.cloneDeep(result.data));
          setCanSave(false);
          setCanRevert(false);
          successMsg('Settings updated successfully!');
          sendHeaderData(result.data);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
      } finally {
        setIsSaving(false);
        setLoadingData(false);
      }
    };

    const handleRevertChanges = () => {
      setIsSaving(true);
      setNewSettingsData(_.cloneDeep(ogSettingsData)); // Revert to original settings
      setCanSave(false); // Disable "Save Changes"
      setCanRevert(false); // Disable "Revert Changes"
      setIsSaving(false);
    };
    

  const scrollToBottom = () => {
    if (settingsWrapperRef.current) {
      settingsWrapperRef.current.scrollTo({
        top: settingsWrapperRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  function formatPhoneNumber(value) {
    if (!value) return value;

    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)})-${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)})-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }

const handleToggleChange = (policyName, state) => {
  setNewSettingsData((prev) => ({
    ...prev,
    shopInfo: {
      ...prev.shopInfo,
      [policyName]: {
        ...prev.shopInfo[policyName],
        enabled: state,
        text: state ? prev.shopInfo[policyName].text : '',
      },
    },
  }));
};

  const handleTextareaChange = (policyName, text) => {
    setNewSettingsData((prev) => ({
      ...prev,
      shopInfo: {
        ...prev.shopInfo,
        [policyName]: {
          ...prev.shopInfo[policyName],
          text
        },
      },
    }));
  };

  const isCustomURLCooldown = () => {
    if (!newSettingsData.urlHandleLastUpdated) return false;
  
    const lastUpdated = new Date(newSettingsData.urlHandleLastUpdated);
    const cooldownEnd = new Date(lastUpdated.getTime() + 7 * 24 * 60 * 60 * 1000); // Add 7 days
    const now = new Date();
  
    return now < cooldownEnd ? cooldownEnd : false;
  };
  
  const formatCooldownDate = (cooldownDate) => {
    const now = new Date();
    const timeDiff = cooldownDate - now;
  
    if (timeDiff <= 0) return "";
  
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
  
    if (minutes < 60) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else if (hours < 24) {
      return `${hours} hr${hours !== 1 ? 's' : ''}`;
    } else {
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
  };
  

  if (hasError || isSaving || loadingData || !ogSettingsData || !newSettingsData || !session || status !== 'authenticated') {
    return (
      <section id={styles.loading}>
        <BounceLoader size={100} color="#154734" />
      </section>
    );
  }

  return (
    <section id={styles.settingsWrapper} ref={settingsWrapperRef}>
      {showSaveIcons && (
        <div id={styles.save} onClick={scrollToBottom}>
          <FontAwesomeIcon icon={faSave} id={styles.icon} />
          <FontAwesomeIcon icon={faChevronDown} id={styles.icon} />
        </div>
      )}

      <h1 className={styles.titleContainer}>Brand Settings</h1>
      <div id={styles.brandSettings}>
        <section className={styles.shopInfoFirst}>
          <section>
            <ProfileUploader 
              newSettingsData={newSettingsData} 
              setNewSettingsData={setNewSettingsData} 
              ogSettingsData={ogSettingsData}
            />
            <BasicInfoOne 
              newSettingsData={newSettingsData} 
              setNewSettingsData={setNewSettingsData} 
              businessCategories={businessCategories} 
            />
          </section>

          <section>
            <Textarea
              value={newSettingsData.aboutUs}
              onChange={(text) => setNewSettingsData({ ...newSettingsData, aboutUs: text })}
              minHeight="100%"
              maxHeight="100%"
              resize="none"
              minCharacters={aboutUsMRC.min}
              maxCharacters={aboutUsMRC.max}
              scrollableParentClass={styles.settingsWrapper}
            />
          </section>
        </section>

        <section className={styles.shopInfoSecond}>
          <h1>Home Banner</h1>
          <h2>Upload <b>3360x840</b> banner dimensions for better quality</h2>
          <BannerUploader 
            newSettingsData={newSettingsData} 
            setNewSettingsData={setNewSettingsData} 
            ogSettingsData={ogSettingsData}
          />
        </section>
      </div>

      <hr />

      <h1 className={styles.titleContainer}>Contact Info</h1>
      <div id={styles.contactInfo}>
        <div>
          <FontAwesomeIcon icon={faPhone} id={styles.icon} />
          <input
            type='text'
            placeholder="(254)-900-0000"
            value={newSettingsData.contactInfo.phone}
            onChange={(e) => {
              const formattedPhone = formatPhoneNumber(e.target.value);
              setNewSettingsData({
                ...newSettingsData,
                contactInfo: {
                  ...newSettingsData.contactInfo,
                  phone: formattedPhone,
                },
              });
            }}
          />
        </div>

        <div>
          <FontAwesomeIcon icon={faEnvelope} id={styles.icon} />
          <input
            type='email'
            placeholder="contact@business.com"
            value={newSettingsData.contactInfo.email}
            onChange={(e) => setNewSettingsData({
              ...newSettingsData,
              contactInfo: {
                ...newSettingsData.contactInfo,
                email: e.target.value,
              },
            })}
          />
        </div>

        <div>
          <FontAwesomeIcon icon={faLocationDot} id={styles.icon} />
          <input
            type='text'
            placeholder="Remote OR Address"
            value={newSettingsData.contactInfo.location}
            onChange={(e) => setNewSettingsData({
              ...newSettingsData,
              contactInfo: {
                ...newSettingsData.contactInfo,
                location: e.target.value,
              },
            })}
          />
        </div>
      </div>

      <hr />
      <h1 className={styles.titleContainer}>Policy & Shipping</h1>

      <div id={styles.shopInfoPolicies}>
        <section className={styles.container}>
          <div>
            <h1>Privacy Policy</h1>
            <ToggleSwitch
              isDisabled={isSaving}
              state={newSettingsData.shopInfo.privacyPolicy.enabled}
              setState={(state) => handleToggleChange('privacyPolicy', state)}
            />
          </div>
          <Textarea
            value={newSettingsData.shopInfo.privacyPolicy.text}
            onChange={(text) => handleTextareaChange('privacyPolicy', text)}
            placeholder="Please enter your privacy policy here."
            resize="vertical" // Allows vertical resizing only
            minHeight="200px"
            maxHeight="400px"
            minCharacters={privacyPolicyMRC.min}
            maxCharacters={privacyPolicyMRC.max}
            showComponent={newSettingsData.shopInfo.privacyPolicy.enabled}
            scrollableParentClass={styles.settingsWrapper}
          />
        </section>

        <section className={styles.container}>
          <div>
            <h1>Return Policy</h1>
            <ToggleSwitch
              isDisabled={isSaving}
              state={newSettingsData.shopInfo.returnPolicy.enabled}
              setState={(state) => handleToggleChange('returnPolicy', state)}
            />
          </div>
          <Textarea
            value={newSettingsData.shopInfo.returnPolicy.text}
            onChange={(text) => handleTextareaChange('returnPolicy', text)}
            placeholder="Enter your return policies here."
            resize="vertical" // Allows vertical resizing only
            minHeight="200px"
            maxHeight="400px"
            minCharacters={returnPolicyMRC.min}
            maxCharacters={returnPolicyMRC.max}
            showComponent={newSettingsData.shopInfo.returnPolicy.enabled}
            scrollableParentClass={styles.settingsWrapper}
          />
        </section>

        <section className={styles.container}>
          <div>
            <h1>Cancellation Policy</h1>
            <ToggleSwitch
              isDisabled={isSaving}
              state={newSettingsData.shopInfo.cancellationPolicy.enabled}
              setState={(state) => handleToggleChange('cancellationPolicy', state)}
            />
          </div>
          <Textarea
            value={newSettingsData.shopInfo.cancellationPolicy.text}
            onChange={(text) => handleTextareaChange('cancellationPolicy', text)}
            placeholder="Enter in your cancellation policy here"
            resize="vertical" // Allows vertical resizing only
            minHeight="200px"
            maxHeight="400px"
            minCharacters={cancellationPolicyMRC.min}
            maxCharacters={cancellationPolicyMRC.max}
            showComponent={newSettingsData.shopInfo.cancellationPolicy.enabled}
            scrollableParentClass={styles.settingsWrapper}
          />
        </section>

        <section className={styles.container}>
          <div>
            <h1>Frequently Asked Questions</h1>
            <ToggleSwitch
              isDisabled={isSaving}
              state={newSettingsData.shopInfo.shopFAQs.enabled}
              setState={(state) => handleToggleChange('shopFAQs', state)}
            />
          </div>
          <Textarea
            value={newSettingsData.shopInfo.shopFAQs.text}
            onChange={(text) => handleTextareaChange('shopFAQs', text)}
            placeholder={`Write your FAQ content how you desire here, additionally, you can use the Q&A Format below for an optimized Q&A section on your page. \n\nQ: This is my question \nA: This is my answer`}
            resize="vertical"
            minHeight="200px"
            maxHeight="400px"
            minCharacters={faqMRC.min}
            maxCharacters={faqMRC.max}
            showComponent={newSettingsData.shopInfo.shopFAQs.enabled}
            scrollableParentClass={styles.settingsWrapper}
          />
        </section>
      </div>



      <hr />
      <h1 className={styles.titleContainer}>Social Links</h1>

      <div id={styles.socialInfo}>
        <section>
          <div>
            <FontAwesomeIcon icon={faFacebook} id={styles.icon} />
            <input
              type='text'
              placeholder="Your Facebook Link"
              value={newSettingsData.socialMedia.facebook}
              onChange={(e) => setNewSettingsData({
                ...newSettingsData,
                socialMedia: {
                  ...newSettingsData.socialMedia,
                  facebook: e.target.value,
                },
              })}
            />
          </div>

          <div>
            <FontAwesomeIcon icon={faInstagram} id={styles.icon} />
            <input
              type='text'
              placeholder="Your Instagram Link"
              value={newSettingsData.socialMedia.instagram}
              onChange={(e) => setNewSettingsData({
                ...newSettingsData,
                socialMedia: {
                  ...newSettingsData.socialMedia,
                  instagram: e.target.value,
                },
              })}
            />
          </div>
        </section>

        <section>
          <div>
            <FontAwesomeIcon icon={faYelp} id={styles.icon} />
            <input
              type='text'
              placeholder="Your Yelp Link"
              value={newSettingsData.socialMedia.yelp}
              onChange={(e) => setNewSettingsData({
                ...newSettingsData,
                socialMedia: {
                  ...newSettingsData.socialMedia,
                  yelp: e.target.value,
                },
              })}
            />
          </div>

          <div>
            <FontAwesomeIcon icon={faGoogle} id={styles.icon} />
            <input
              type='text'
              placeholder="Your Google Link"
              value={newSettingsData.socialMedia.google}
              onChange={(e) => setNewSettingsData({
                ...newSettingsData,
                socialMedia: {
                  ...newSettingsData.socialMedia,
                  google: e.target.value,
                },
              })}
            />
          </div>
        </section>
      </div>

      <hr />

      <h1 className={styles.titleContainer}>Website Settings</h1>
      <div id={styles.websiteSettings}>
      <div className={styles.link}>
        <section id={styles.shopVisibleWrap}>
          <h1>
            <FontAwesomeIcon icon={faLink} id={styles.icon} /> Custom URL
          </h1>
          {isCustomURLCooldown() && (
            <h2>
              <FontAwesomeIcon icon={faExclamationTriangle} id={styles.icon} color='#ffb005' />
              Cannot update until{' '}
              <b>{formatCooldownDate(isCustomURLCooldown())}</b>
            </h2>
          )}
        </section>
        <section>
          <div>
            <h1>vendors/</h1>
            <input
              maxLength={30}
              minLength={5}
              type="text"
              placeholder={newSettingsData.urlHandle}
              value={newSettingsData.urlHandle}
              disabled={!!isCustomURLCooldown()} // Disable the input field if the cooldown is active
              onChange={(e) => {
                const value = e.target.value;
                const isValid = /^[a-zA-Z0-9]*$/.test(value); // Allow alphanumeric characters
                if (isValid) {
                  setNewSettingsData({
                    ...newSettingsData,
                    urlHandle: value,
                  });
                }
              }}
            />
          </div>
        </section>
      </div>


        <div className={styles.link}>
          <section style={{ display: 'flex', flexDirection: 'column' }}>
            <h1><FontAwesomeIcon icon={faMapLocationDot} id={styles.icon} /> ZIP Code</h1>
            <h2>Get found on the <b>Vendor Maps</b></h2>
          </section>

          <section>
            <div>
              <NumberFormat
              
                format={newSettingsData.zip && newSettingsData.zip.length > 5 ? '#####-####' : '#####'}
                placeholder="ZIP CODE"
                mask="_"
                value={newSettingsData.zip}
                onValueChange={({ value }) => {
                  const isValid = /^\d{0,9}$/.test(value); // Allow only numeric input up to 9 digits
                  if (isValid) {
                    setNewSettingsData({
                      ...newSettingsData,
                      zip: value,
                    });
                  }
                }}
                className={styles.zipInput}
              />
            </div>
          </section>
        </div>

        <div className={styles.toggle}>
          <section id={styles.shopVisibleWrap}>
            <h1><FontAwesomeIcon icon={faDoorOpen} id={styles.icon} /> Shop Visible</h1>
            <h2 style={{ display: !isShopVisibleEnabled() ? 'flex' : 'none' }}><FontAwesomeIcon icon={faExclamationTriangle} id={styles.icon} />Complete Brand & Contact Settings</h2>
          </section>

          <ToggleSwitch
            isDisabled={!isShopVisibleEnabled()}
            state={newSettingsData.pagesEnabled.shop}
            setState={() =>
              setNewSettingsData((cur) => ({
                ...cur,
                pagesEnabled: {
                  ...cur.pagesEnabled,
                  shop: !cur.pagesEnabled.shop,
                },
              }))
            }
          />
        </div>

        <div className={styles.toggle}>
          <h1><FontAwesomeIcon icon={faMoneyCheckDollar} id={styles.icon} /> Taxes On Products</h1>
          <ToggleSwitch
            isDisabled={false}
            state={newSettingsData.shopTaxes.productTax}
            setState={() =>
              setNewSettingsData((cur) => ({
                ...cur,
                shopTaxes: {
                  ...cur.shopTaxes,
                  productTax: !cur.shopTaxes.productTax,
                },
              }))
            }
          />
        </div>

        <div className={styles.toggle}>
          <h1><FontAwesomeIcon icon={faMoneyCheckDollar} id={styles.icon} /> Taxes On Services</h1>
          <ToggleSwitch
            isDisabled={false}
            state={newSettingsData.shopTaxes.serviceTax}
            setState={() =>
              setNewSettingsData((cur) => ({
                ...cur,
                shopTaxes: {
                  ...cur.shopTaxes,
                  serviceTax: !cur.shopTaxes.serviceTax,
                },
              }))
            }
          />
        </div>

        <div className={styles.toggle}>
          <h1><FontAwesomeIcon icon={faCartShopping} id={styles.icon} /> Products Page Visibility</h1>
          <ToggleSwitch
            isDisabled={false}
            state={newSettingsData.pagesEnabled.products}
            setState={() =>
              setNewSettingsData((cur) => ({
                ...cur,
                pagesEnabled: {
                  ...cur.pagesEnabled,
                  products: !cur.pagesEnabled.products,
                },
              }))
            }
          />
        </div>

        <div className={styles.toggle}>
          <h1><FontAwesomeIcon icon={faTools} id={styles.icon} /> Services Page Visibility</h1>
          <ToggleSwitch
            isDisabled={false}
            state={newSettingsData.pagesEnabled.services}
            setState={() =>
              setNewSettingsData((cur) => ({
                ...cur,
                pagesEnabled: {
                  ...cur.pagesEnabled,
                  services: !cur.pagesEnabled.services,
                },
              }))
            }
          />
        </div>
      </div>

      <hr />

      <div id={styles.actionGroup}>
        <button
          className={styles.saveBtn}
          disabled={!canSave} // Based on `canSave` state
          onClick={handleSubmit}
        >
          Save Changes
        </button>
        <button
          className={styles.saveBtn}
          disabled={!canRevert} // Based on `canRevert` state
          onClick={handleRevertChanges}
        >
          Revert Changes
        </button>
      </div>

      <hr />
    </section>
  );
}


const BasicInfoOne = ({ newSettingsData, setNewSettingsData, businessCategories }) => {
  
  const handleDisplayNameChange = (value) => {
    // Allow letters, numbers, hyphens, ampersands, and spaces
    const isValid = /^[a-zA-Z0-9\-& ]*$/.test(value);
    if (isValid) {
      setNewSettingsData({
        ...newSettingsData,
        displayName: value,
      });
    }
  };
  
  const sanitizeDisplayName = () => {
    // Allow letters, numbers, hyphens, ampersands, and spaces
    const sanitizedValue = newSettingsData.displayName.replace(/[^a-zA-Z0-9\-& ]/g, '');
    setNewSettingsData({
      ...newSettingsData,
      displayName: sanitizedValue,
    });
  };

  const handleSelectedBusinessCategory = (category) => {
    setNewSettingsData({
      ...newSettingsData,
      category: category,
    });
  };

  return (
    <div id={styles.basicInfoOne}>
      <TextInput value={newSettingsData.displayName} onChange={handleDisplayNameChange} onBlur={sanitizeDisplayName} placeholder="My Shop" minCharacters={5} maxCharacters={32} />
      <BusinessCategoryDropMenu 
        title="Business Category" 
        items={businessCategories} 
        onSelect={handleSelectedBusinessCategory} 
        selected={newSettingsData.category} 
      />
    </div>
  );
};


const BannerUploader = ({ newSettingsData, setNewSettingsData, ogSettingsData }) => {
  const [file, setFile] = useState(newSettingsData.banner || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFile(newSettingsData.banner); // Update file when newSettingsData changes
  }, [newSettingsData]);

  const fileHandler = async (e) => {
    if (typeof window === 'undefined') return;

    const getFile = e.target.files[0];
    const fileSize = (getFile.size / (1024 * 1024)).toFixed(2);
    if (fileSize > 4) {
      errorMsg('Images must be no larger than 4mb');
      return;
    }

    setLoading(true);

    const heic2any = require('heic2any');
    const getRevisedFile = getFile.name.includes('.HEIC')
      ? await heic2any({ blob: getFile, toType: 'image/jpeg' })
      : getFile;

    const getBackendBase64Data = await resizeFile(getRevisedFile, 'base64');
    setNewSettingsData({
      ...newSettingsData,
      banner: getBackendBase64Data,
    });

    const getDisplayImg = await resizeFile(getRevisedFile, 'blob');
    const objectUrl = URL.createObjectURL(getDisplayImg);

    setFile(objectUrl); // Update the file to display
    setLoading(false);
    
    return () => URL.revokeObjectURL(objectUrl);
  };

  const removeImage = () => {
    setFile(null); // Clear the file state
    setNewSettingsData({
      ...newSettingsData,
      banner: null, // Clear the banner image in the settings data
    });
  };

  const resizeFile = (file, type) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        3360,
        840,
        'PNG',
        100,
        0,
        (uri) => resolve(uri),
        type,
        300,
        0
      );
    });

  return (
    <motion.div id={styles.bannerUploader}>
      {loading ? (
        <PuffLoader color="#154734" />
      ) : !file ? (
        <span>
          <FontAwesomeIcon icon={faUpload} id={styles.icon} />
          <input type="file" onChange={fileHandler} />
        </span>
      ) : (
        <span>
          <FontAwesomeIcon onClick={removeImage} icon={faXmarkCircle} id={styles.removeImg} />
          <Image fill style={{ objectFit: 'cover' }} src={file} alt={file} />
        </span>
      )}
    </motion.div>
  );
};


const ProfileUploader = ({ newSettingsData, setNewSettingsData, ogSettingsData }) => {
  const [file, setFile] = useState(newSettingsData.pfp || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFile(newSettingsData.pfp); // Update file when newSettingsData changes
  }, [newSettingsData]);

  const fileHandler = async (e) => {
    if (typeof window === 'undefined') return;

    const getFile = e.target.files[0];
    const fileSize = (getFile.size / (1024 * 1024)).toFixed(2);
    if (fileSize > 4) {
      errorMsg('Images must be no larger than 4mb');
      return;
    }

    setLoading(true);

    const heic2any = require('heic2any');
    const getRevisedFile = getFile.name.includes('.HEIC')
      ? await heic2any({ blob: getFile, toType: 'image/jpeg' })
      : getFile;

    const getBackendBase64Data = await resizeFile(getRevisedFile, 'base64');
    setNewSettingsData({
      ...newSettingsData,
      pfp: getBackendBase64Data,
    });

    const getDisplayImg = await resizeFile(getRevisedFile, 'blob');
    const objectUrl = URL.createObjectURL(getDisplayImg);

    setFile(objectUrl); // Update the file to display
    setLoading(false);
    
    return () => URL.revokeObjectURL(objectUrl);
  };

  const removeImage = () => {
    setFile(null); // Clear the file state
    setNewSettingsData({
      ...newSettingsData,
      pfp: null, // Clear the profile picture in the settings data
    });
  };

  const resizeFile = (file, type) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        750,
        750,
        'PNG',
        100,
        0,
        (uri) => resolve(uri),
        type,
        300,
        0
      );
    });

  return (
    <motion.div id={styles.profileUploader} whileHover={{ backgroundColor: '#43a882', color: '#FFFFFF' }}>
      {loading ? (
        <span style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}><PuffLoader color="#154734" /></span>
      ) : !file ? (
        <span>
          <FontAwesomeIcon icon={faUpload} id={styles.icon} />
          <input type="file" onChange={fileHandler} />
        </span>
      ) : (
        <span>
          <FontAwesomeIcon onClick={removeImage} icon={faXmarkCircle} id={styles.removeImg} />
          <Image fill style={{ objectFit: 'cover' }} src={file} alt={file} />
        </span>
      )}
    </motion.div>
  );
};


const BusinessCategoryDropMenu = ({ items, title, onSelect, selected }) => {
  const [navDropMenu, setNavDropMenu] = useState(false);

  const handleSelection = (e, item) => {
    e.preventDefault();
    onSelect(item);
    setNavDropMenu(false);
  };

  return (
    <div onMouseLeave={() => setNavDropMenu(false)} id={styles.categoryDrop}>
      <button 
        onClick={(e) => { e.preventDefault(); setNavDropMenu(!navDropMenu); }} 
        className={styles.dropdownBtn}
      >
        <FontAwesomeIcon icon={faChevronDown} className={styles.icon} /> 
        {selected || title}
      </button>
      <div style={{ display: navDropMenu ? 'block' : 'none' }} className={styles.dropdownContent}>
        {items.map((item, index) => (
          <button key={index} onClick={(e) => handleSelection(e, item)}>
            {item}
          </button>
        ))}
      </div>
    </div>
  );
};
